import React, { PureComponent } from 'react';
import NotificationSystem from 'rc-notification';
import moment from 'moment';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { BasicNotification } from '../../../../shared/components/Notification';
import config from '../../../../config';
import TableBuilder from '../../../../shared/components/table/TableBuilder';
import { updateCalendarActions } from '../../../../redux/actions/index';
import { SellerSelect, User } from '../../../../shared/prop-types/MainProps';

const apiUrl = config.isProdEnv ? config.serverProdUrl : config.serverDevUrl;

let notificationLU = null;
let notificationRU = null;
let notificationTC = null;

const trueFalseMappings = {
  true: 'True',
  false: 'False',
};

function extractValues(mappings) {
  return Object.keys(mappings);
}

function lookupValue(mappings, key) {
  return mappings[key];
}

function lookupKey(mappings, name) {
  // eslint-disable-next-line
  for (let key in mappings) {
    // eslint-disable-next-line
    if (mappings.hasOwnProperty(key)) {
      if (name === mappings[key]) {
        return key;
      }
    }
  }
  return null;
}

class CalendarManagementTable extends PureComponent {
  static propTypes = {
    sellerSelect: SellerSelect.isRequired,
    user: User.isRequired,
    dispatch: PropTypes.func.isRequired,
  }

  constructor(props) {
    super(props);

    this.state = {
      columnDefs: [
        {
          field: 'calendar_id',
          headerName: '#',
          width: 80,
        },
        {
          field: 'title',
          headerName: 'Description',
          editable: true,
        },
        {
          field: 'all_day',
          headerName: 'All Day',
          editable: true,
          cellEditor: 'agSelectCellEditor',
          cellEditorParams: {
            values: extractValues(trueFalseMappings),
            useFormatter: true,
          },
          valueFormatter: (params) => {
            const dispalyName = lookupValue(trueFalseMappings, params.value);
            return dispalyName;
          },
          valueParser: (params) => {
            lookupKey(trueFalseMappings, params.newValue);
          },
          width: 80,
        },
        {
          field: 'start',
          headerName: 'Start',
          editable: true,
          cellEditorParams: {
            useFormatter: true,
          },
          valueFormatter: (params) => {
            // strip off time to keep it clean
            const dispalyDate = params.value.substring(0, params.value.indexOf(':00.000')).replace('T', ' ');
            return dispalyDate;
          },
          valueParser: (params) => {
            // Later add timezone logic?
            let dataValue = `${params.newValue}:00.000Z`.replace(' ', 'T');
            if (params.newValue.indexOf('T') > 0 && params.newValue.indexOf('Z') > 0) { // Maybe add a regex later.
              dataValue = params.newValue; // User is passing in full string
            }
            return dataValue;
          },
          width: 130,
        },
        {
          field: 'end',
          headerName: 'End',
          editable: true,
          cellEditorParams: {
            useFormatter: true,
          },
          valueFormatter: (params) => {
            // strip off time to keep it clean
            const dispalyDate = params.value.substring(0, params.value.indexOf(':00.000')).replace('T', ' ');
            return dispalyDate;
          },
          valueParser: (params) => {
            // Later add timezone logic?
            let dataValue = `${params.newValue}:00.000Z`.replace(' ', 'T');
            if (params.newValue.indexOf('T') > 0 && params.newValue.indexOf('Z') > 0) { // Maybe add a regex later.
              dataValue = params.newValue; // User is passing in full string
            }
            return dataValue;
          },
          width: 130,
        },
        {
          field: 'priority',
          headerName: 'Priority',
          editable: true,
          width: 80,
        },
        {
          field: 'owner',
          headerName: 'Owner',
          width: 80,
        },
        {
          field: 'seller_account_id',
          headerName: 'SAID',
          width: 80,
          hide: true,
        },
      ],
      defaultColDef: {
        sortable: true,
        filter: true,
        resizable: true,
      },
      showDatePicker: false,
      showFooter: false,
      showAddRowButton: true,
      showDeleteSelectedRowButton: true,
      autoSizeToFit: true,
    };
  }

  componentDidMount() {
    // eslint-disable-next-line
    NotificationSystem.newInstance({}, n => notificationLU = n);
    // eslint-disable-next-line
    NotificationSystem.newInstance({}, n => notificationRU = n);
    // eslint-disable-next-line
    NotificationSystem.newInstance({}, n => notificationTC = n);
  }

  componentWillUnmount() {
    if (notificationLU) {
      notificationLU.destroy();
    }
    if (notificationLU) {
      notificationRU.destroy();
    }
    if (notificationLU) {
      notificationTC.destroy();
    }
  }

  // eslint-disable-next-line
  show = (color, title, msg) => {
    return config.showNotification({
      notification: <BasicNotification
        color={color}
        title={title}
        message={msg}
      />,
      position: 'right-up',
      notificationLU,
      notificationRU,
      notificationTC,
    });
  };

  getNewRow = () => {
    const { user, sellerSelect } = this.props;

    const newData = {
      calendar_id: 0,
      title: '',
      all_day: 'true',
      start: moment.utc().startOf('day').format('YYYY-MM-DDTHH:mm:ss.SSSZ'),
      end: moment.utc().startOf('day').format('YYYY-MM-DDTHH:mm:ss.SSSZ'),
      priority: 'normal',
      owner: Number(user.id),
      seller_account_id: Number(sellerSelect.value),
    };
    return newData;
  }

  parentProcessChange = (rowNode, user, sellerSelect, apiEndPoint, childThis) => {
    const { dispatch } = this.props;
    const headerWithAuth = { ...config.jsonHeader, authorization: `JWT ${user.jwtToken}` };
    // Add logic to check input values, make sure they are present and correct.
    const requestOptions = {
      method: 'POST',
      headers: headerWithAuth,
      body: JSON.stringify({
        calendarId: rowNode.data.calendar_id,
        title: rowNode.data.title,
        allDay: rowNode.data.all_day,
        start: rowNode.data.start,
        end: rowNode.data.end,
        priority: rowNode.data.priority,
        owner: rowNode.data.owner?.toString(),
        sellerAccountId: rowNode.data.seller_account_id?.toString(),
      }),
    };

    if ((rowNode.data.calendar_id || rowNode.data.calendar_id === 0) && rowNode.data.title && rowNode.data.all_day && rowNode.data.start
      && rowNode.data.end && rowNode.data.priority) {
      // Call via API (would like to update to " await ")
      fetch(`${apiUrl}/events/calendar`, requestOptions)
        .then((results) => {
          if (!results.ok) {
            throw Error(results.statusText);
          }
          return results.json();
        }).then((data) => {
          // success message
          if (rowNode.data.calendar_id === 0) {
            this.show('primary', 'Success', `Calendar entry ${data[0].calendar_id} has been created.`);
            childThis.refreshData();
          } else {
            this.show('primary', 'Success', `Calendar entry ${data[0].calendar_id} has been updated.`);
          }
          dispatch(updateCalendarActions.updateCalanderChange('UPDATE_CALENDAR'));
        }).catch((error) => {
          this.show('danger', 'Error', `Problem adding/updating Calendar entry. \n Refrence: ${error}`);
        });
    } else {
      this.show('danger', 'Error', 'Did not process, all fields must be filled out.');
    }
  }

  parentDeleteRows = (selectedRows, user, apiEndPoint, childThis) => {
    const { dispatch } = this.props;
    const headerWithAuth = { ...config.jsonHeader, authorization: `JWT ${user.jwtToken}` };
    // Add logic to check input values, make sure they are present and correct.
    const promises = [];

    selectedRows.forEach((selectedRow) => {
      const requestOptions = {
        method: 'DELETE',
        headers: headerWithAuth,
      };

      const fulDeleteUrl = `${apiUrl}/events/calendar/${selectedRow.calendar_id}/seller/${selectedRow.seller_account_id}`;

      // Call via API (would like to update to " await ")
      promises.push(fetch(fulDeleteUrl, requestOptions)
        .then((results) => {
          if (!results.ok) {
            throw Error(results.statusText);
          }
          return results.json();
        }).then(() => {
          // success message
          this.show('primary', 'Success', `Calendar entry ${selectedRow.calendar_id} has been deleted.`);
        }).catch((error) => {
          this.show('danger', 'Error', `Problem deleting Calendar entry. \n Refrence: ${error}`);
        }));
    });
    if (promises.length > 0) {
      Promise.all(promises).then(() => {
        // All good.
        childThis.refreshData();
        dispatch(updateCalendarActions.updateCalanderChange('UPDATE_CALENDAR'));
      }, () => {
        childThis.refreshData();
      });
    }
  }

  render() {
    const {
      columnDefs, defaultColDef, showDatePicker, showFooter, showAddRowButton, showDeleteSelectedRowButton, autoSizeToFit,
    } = this.state;

    const apiEndPoint = `${apiUrl}/events/calendar/seller`;

    return (
      <TableBuilder
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        apiEndPoint={apiEndPoint}
        showDatePicker={showDatePicker}
        showFooter={showFooter}
        parentProcessChange={this.parentProcessChange}
        parentDeleteRows={this.parentDeleteRows}
        showAddRowButton={showAddRowButton}
        showDeleteSelectedRowButton={showDeleteSelectedRowButton}
        getNewRow={this.getNewRow}
        autoSizeToFit={autoSizeToFit}
        confirmLogic
      />
    );
  }
}

const mapStateToProps = (state) => {
  const { sellerSelect } = state;
  const { user } = state.authentication;

  return {
    sellerSelect,
    user,
  };
};

export default connect(mapStateToProps)(CalendarManagementTable);
